import { useMemo, VFC } from "react";

// Libraries
import { DateTime } from "luxon";
import { Column } from "react-table";

// API
import { GetBlockchainAnimalDataByKillNumber_blockchainAnimalDataByKillNumber_carcassGrading } from "api/Blockchain/types/GetBlockchainAnimalDataByKillNumber";
import { GetBlockchainAnimalDataByPassport_blockchainAnimalDataByPassport_carcassGrading } from "api/Blockchain/types/GetBlockchainAnimalDataByPassport";

// Utils
import { TABLE_IDS } from "constants/Interface";

// Components
import { Spacer, Table, Text, Title } from "components";
import { AnimalDataTypes } from "../";

type AnimalCarcassGradingType =
  | GetBlockchainAnimalDataByPassport_blockchainAnimalDataByPassport_carcassGrading
  | GetBlockchainAnimalDataByKillNumber_blockchainAnimalDataByKillNumber_carcassGrading;

interface BlockchainAnimalCarcassGradingTableProps {
  carcassGrading: AnimalDataTypes["carcassGrading"];
}

const COLUMN_IDS = {
  HOLDING_ID: "holdingId",
  HOT_WEIGHT: "hotWeight",
  COLD_WEIGHT: "coldWeight",
  DATE: "date",
  CREATED_BY: "createdBy",
  GRADING: "grading",
  FAT_SCORE: "fatScore",
  CONFORMATION: "conformation",
  CONDEMNED_WEIGHT: "condemnedWeight",
  VALUE: "value",
  KILL_NUMBER: "killNumber",
  LOT_NUMBER: "lotNumber",
};

export const BlockchainAnimalCarcassGradingTable: VFC<BlockchainAnimalCarcassGradingTableProps> = ({
  carcassGrading,
}) => {
  const data = useMemo(() => carcassGrading, []);

  //@ts-expect-error
  const columns: Column<AnimalCarcassGradingType>[] = useMemo(
    () => [
      {
        id: COLUMN_IDS.HOLDING_ID,
        Header: "Holding Id",
        accessor: "holdingId",
      },
      {
        id: COLUMN_IDS.HOT_WEIGHT,
        Header: "Hot Weight",
        accessor: "hotWeight",
      },
      {
        id: COLUMN_IDS.COLD_WEIGHT,
        Header: "Cold Weight",
        accessor: "coldWeight",
      },
      {
        id: COLUMN_IDS.DATE,
        Header: "Date",
        accessor: "date",
        Cell: ({ row }) =>
          row.values[COLUMN_IDS.DATE] ? DateTime.fromISO(row.values[COLUMN_IDS.DATE]).toLocaleString() : "\u2014",
      },
      {
        id: COLUMN_IDS.CREATED_BY,
        Header: "Created By",
        accessor: "createdBy",
      },
      {
        id: COLUMN_IDS.GRADING,
        Header: "Grading",
        accessor: "grading",
      },
      {
        id: COLUMN_IDS.FAT_SCORE,
        Header: "Fat Score",
        accessor: "fatScore",
      },
      {
        id: COLUMN_IDS.CONFORMATION,
        Header: "Conformation",
        accessor: "conformation",
      },
      {
        id: COLUMN_IDS.CONDEMNED_WEIGHT,
        Header: "Condemned Weight",
        accessor: "condemnedWeight",
      },
      {
        id: COLUMN_IDS.VALUE,
        Header: "Value",
        accessor: "value",
      },
      {
        id: COLUMN_IDS.KILL_NUMBER,
        Header: "Kill Number",
        accessor: "killNumber",
      },
      {
        id: COLUMN_IDS.LOT_NUMBER,
        Header: "Lot Number",
        accessor: "lotNumber",
      },
    ],
    [],
  );

  return (
    <>
      <Title secondary>Carcass Grading</Title>

      <Spacer baselineHeight={1} />

      {data?.length ? (
        <Table<AnimalCarcassGradingType>
          columns={columns}
          data={data}
          tableId={TABLE_IDS.BLOCKCHAIN_ANIMAL_CARCASS_GRADING}
        />
      ) : (
        <Text smallest>
          <em>No Carcass Grading Data for this Animal</em>
        </Text>
      )}
    </>
  );
};
